import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { CommunityGate } from '~/interfaces/community';

import { PillButton, type PillButtonSize } from './ui/PillButton';
import { PillLink } from './ui/PillLink';

import { type GateListSubscription, useSubscribeToGateListMutation } from '../_hooks/useSubscribeToGateListMutation';

type TFn = ReturnType<typeof useTranslation>['t'];

interface OwnerGateButtonProps {
  gate: CommunityGate | null | undefined;
  spaceSlug?: string;
  size?: PillButtonSize;
  fullWidth?: boolean;
  className?: string;
  onSubscribed?: () => void;
}

type GateListSubscribeButtonProps = Omit<OwnerGateButtonProps, 'gate'> & {
  listName: string;
};

// A list subscription waits for the member's own subscription to be active: confirmed, or resumed when paused.
const notesFor = (subscription: GateListSubscription, listName: string, t: TFn) => {
  const notes: string[] = [];
  if (subscription.resubscribed) notes.push(t('community.list_gate.resubscribed'));
  if (subscription.status !== 'active') {
    const key = subscription.subscription_status === 'paused' ? 'paused' : 'pending';
    notes.push(t(`community.list_gate.${key}`, { listName }));
  }
  return notes;
};

const GateListSubscribeButton: FC<GateListSubscribeButtonProps> = ({
  listName,
  spaceSlug,
  onSubscribed,
  ...pillProps
}) => {
  const { t } = useTranslation();
  const { mutate: subscribe, isPending, data, error } = useSubscribeToGateListMutation({ onSubscribed });
  const notes = error ? [error.message] : data ? notesFor(data, listName, t) : [];

  return (
    <>
      <PillButton variant="primary" disabled={isPending} onClick={() => subscribe({ spaceSlug })} {...pillProps}>
        {isPending ? t('community.list_gate.subscribing') : t('community.list_gate.subscribe', { listName })}
      </PillButton>
      {notes.map((note) => (
        <p key={note} role={error ? 'alert' : 'status'} className="mt-3 text-sm leading-5 opacity-75">
          {note}
        </p>
      ))}
    </>
  );
};

// Subscribing is how anyone gets past a list condition, so without the owner's button the gate offers its list.
export const OwnerGateButton: FC<OwnerGateButtonProps> = ({ gate, spaceSlug, onSubscribed, ...pillProps }) => {
  if (gate?.button_label && gate.button_url) {
    return (
      <PillLink to={gate.button_url} target="_blank" rel="noopener noreferrer" variant="primary" {...pillProps}>
        {gate.button_label}
      </PillLink>
    );
  }

  if (!gate?.subscribable_list_name) return null;

  return (
    <GateListSubscribeButton
      listName={gate.subscribable_list_name}
      spaceSlug={spaceSlug}
      onSubscribed={onSubscribed}
      {...pillProps}
    />
  );
};
